import { useState, useEffect, useRef } from "react";
import axios from "axios";
import {
  motion,
  AnimatePresence,
  useMotionValue,
  useDragControls,
} from "framer-motion";
import { useAuth, getAuthToken } from "../../context/AuthContext";
import { useTheme } from "../../context/ThemeContext";
import { Checkbox } from "../../components/ui/checkbox";
import { Button } from "../ui/button";
import { useMessages } from "../../context/MessageContext";
import { generateOfferteFromRequest } from "../../api/offerte";
import SMTPSettingsDialog from "./SMTPSettingsDialog";
import ComposeEmailDialog from "./ComposeEmailDialog";
import {
  Search,
  Mail,
  FileText,
  RefreshCcw,
  Check,
  AlertCircle,
  X,
  Filter,
  Clock,
  ChevronDown,
  Settings,
  Send,
} from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../ui/select";
import { format, parseISO, isToday, isYesterday } from "date-fns";
import { nl } from "date-fns/locale";
import useContacts from "../../api/contacts";

const InboxManager = () => {
  const { company } = useAuth();
  const { theme } = useTheme();
  const isDark = theme === "dark";
  const {
    getCompanyContacts,
    updateContact,
    updateMultipleContacts,
    deleteContact,
  } = useContacts();
  const [isGeneratingOfferte, setIsGeneratingOfferte] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [composeOpen, setComposeOpen] = useState(false);

  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [activeTab, setActiveTab] = useState("inbox");
  const [expandedMessage, setExpandedMessage] = useState(null);
  const [messageToDelete, setMessageToDelete] = useState(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [statusFilter, setStatusFilter] = useState("all");
  const [sortOrder, setSortOrder] = useState("newest");
  const [selectedMessages, setSelectedMessages] = useState([]);
  const [selectAll, setSelectAll] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [inboxMessages, setInboxMessages] = useState([]);
  const [archivedMessages, setArchivedMessages] = useState([]);
  const [sentMessages, setSentMessages] = useState([]);
  const { setUnreadMessagesCount } = useMessages();

  // Mobile detection for responsive layout
  const [isMobile, setIsMobile] = useState(
    typeof window !== "undefined" ? window.innerWidth < 640 : false,
  );
  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 640);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const API_BASE_URL =
    process.env.REACT_APP_BACKEND_URL || "http://localhost:9000/";

  useEffect(() => {
    fetchAllMessages();
  }, []);

  useEffect(() => {
    // When active tab changes, make sure we have the right messages loaded
    if (activeTab === "inbox") {
      setMessages(inboxMessages);
    } else if (activeTab === "archived") {
      setMessages(archivedMessages);
    } else if (activeTab === "sent") {
      setMessages(sentMessages);
    }
  }, [activeTab, inboxMessages, archivedMessages, sentMessages]);

  // Update unreadMessagesCount whenever inboxMessages changes
  useEffect(() => {
    const unreadCount = inboxMessages.filter((msg) => !msg.read).length;
    setUnreadMessagesCount(unreadCount);
  }, [inboxMessages, setUnreadMessagesCount]);

  const fetchAllMessages = async () => {
    setLoading(true);
    try {
      // Fetch both inbox and archived messages in parallel
      const [inboxData, archiveData] = await Promise.all([
        getCompanyContacts({ status: "Inbox" }),
        getCompanyContacts({ status: "Archive" }),
      ]);

      setInboxMessages(inboxData || []);
      setArchivedMessages(archiveData || []);

      // Fetch Sent Messages
      if (company?.id) {
        try {
          const token = getAuthToken();
          const res = await axios.get(
            `${API_BASE_URL}api/mails/sent/${company.id}`,
            {
              headers: { Authorization: `Bearer ${token}` },
            },
          );

          // Normalize sent messages to match contact structure
          const normalizedSent = (res.data || []).map((msg) => ({
            id: msg.id,
            name: msg.to, // Display recipient as name
            email: msg.to,
            subject: msg.subject,
            message: msg.body, // This is HTML
            created_at: msg.sent_at,
            read: true, // Always read
            type: "sent",
            status: "Sent",
          }));
          setSentMessages(normalizedSent);
        } catch (e) {
          console.error("Error fetching sent emails", e);
        }
      }

      // Set the messages for current tab
      if (activeTab === "inbox") {
        setMessages(inboxData || []);
      } else if (activeTab === "archived") {
        setMessages(archiveData || []);
      } else if (activeTab === "sent") {
        // Will be updated via effect, but good to set here too if we want immediate
        // However, since we fetch async above, logic below works if we use state.
        // Actually, we can't rely on state update immediately.
        // But the useEffect [activeTab, ...] will handle it when sentMessages updates.
      }
    } catch (err) {
      console.error("Error fetching messages:", err);
      setError("Er is een fout opgetreden bij het ophalen van de berichten.");
    } finally {
      setLoading(false);
    }
  };

  const fetchMessages = fetchAllMessages;

  const formatMessageDate = (dateString) => {
    const date = parseISO(dateString);
    if (isToday(date)) {
      return format(date, "p", { locale: nl });
    } else if (isYesterday(date)) {
      return `Gisteren om ${format(date, "p", { locale: nl })}`;
    } else {
      return format(date, "dd MMMM yyyy 'om' p", { locale: nl });
    }
  };

  const handleMarkAsRead = async (id) => {
    try {
      const result = await updateContact(id, { read: true });

      // Update both inboxMessages and archivedMessages
      setInboxMessages(
        inboxMessages.map((msg) =>
          msg.id === id ? { ...msg, read: true } : msg,
        ),
      );
      setArchivedMessages(
        archivedMessages.map((msg) =>
          msg.id === id ? { ...msg, read: true } : msg,
        ),
      );
      setMessages(
        messages.map((msg) => (msg.id === id ? { ...msg, read: true } : msg)),
      );
    } catch (err) {
      console.error("Error marking message as read:", err);
      setError("Er is een fout opgetreden bij het markeren van het bericht.");
    }
  };

  const handleMarkAsUnread = async (id) => {
    try {
      await updateContact(id, { read: false });

      // Update both inboxMessages and archivedMessages
      setInboxMessages(
        inboxMessages.map((msg) =>
          msg.id === id ? { ...msg, read: false } : msg,
        ),
      );
      setArchivedMessages(
        archivedMessages.map((msg) =>
          msg.id === id ? { ...msg, read: false } : msg,
        ),
      );
      setMessages(
        messages.map((msg) => (msg.id === id ? { ...msg, read: false } : msg)),
      );
    } catch (err) {
      console.error("Error marking message as unread:", err);
      setError("Er is een fout opgetreden bij het markeren van het bericht.");
    }
  };

  const handleDeleteMessage = async () => {
    if (!messageToDelete) return;

    setIsSubmitting(true);

    try {
      await deleteContact(messageToDelete.id);

      // Remove from both lists
      setInboxMessages(
        inboxMessages.filter((msg) => msg.id !== messageToDelete.id),
      );
      setArchivedMessages(
        archivedMessages.filter((msg) => msg.id !== messageToDelete.id),
      );
      setMessages(messages.filter((msg) => msg.id !== messageToDelete.id));

      showSuccess("Bericht is succesvol verwijderd.");
      setMessageToDelete(null);
      setDeleteDialogOpen(false);
      setSelectedMessages(
        selectedMessages.filter((id) => id !== messageToDelete.id),
      );
    } catch (err) {
      console.error("Error deleting message:", err);
      setError(
        "Er is een fout opgetreden bij het verwijderen van het bericht.",
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleArchiveMessage = async (id) => {
    try {
      await updateContact(id, { status: "Archive" });

      // Move from inbox to archive
      const messageToMove = inboxMessages.find((msg) => msg.id === id);
      if (messageToMove) {
        setInboxMessages(inboxMessages.filter((msg) => msg.id !== id));
        setArchivedMessages([
          ...archivedMessages,
          { ...messageToMove, status: "Archive" },
        ]);
      }

      // Update current view
      if (activeTab === "inbox") {
        setMessages(messages.filter((msg) => msg.id !== id));
      }

      showSuccess("Bericht is gearchiveerd.");
      setSelectedMessages(
        selectedMessages.filter((selectedId) => selectedId !== id),
      );
    } catch (err) {
      console.error("Error archiving message:", err);
      setError("Er is een fout opgetreden bij het archiveren van het bericht.");
    }
  };

  const handleUnarchiveMessage = async (id) => {
    try {
      await updateContact(id, { status: "Inbox" });

      // Move from archive to inbox
      const messageToMove = archivedMessages.find((msg) => msg.id === id);
      if (messageToMove) {
        setArchivedMessages(archivedMessages.filter((msg) => msg.id !== id));
        setInboxMessages([
          ...inboxMessages,
          { ...messageToMove, status: "Inbox" },
        ]);
      }

      // Update current view
      if (activeTab === "archived") {
        setMessages(messages.filter((msg) => msg.id !== id));
      }

      showSuccess("Bericht is teruggezet naar inbox.");
      setSelectedMessages(
        selectedMessages.filter((selectedId) => selectedId !== id),
      );
    } catch (err) {
      console.error("Error unarchiving message:", err);
      setError(
        "Er is een fout opgetreden bij het terugzetten van het bericht.",
      );
    }
  };

  const handleBatchAction = async (action) => {
    if (selectedMessages.length === 0) return;

    setIsSubmitting(true);

    try {
      if (action === "read") {
        await updateMultipleContacts(selectedMessages, { read: true });

        // Update both arrays
        setInboxMessages(
          inboxMessages.map((msg) =>
            selectedMessages.includes(msg.id) ? { ...msg, read: true } : msg,
          ),
        );
        setArchivedMessages(
          archivedMessages.map((msg) =>
            selectedMessages.includes(msg.id) ? { ...msg, read: true } : msg,
          ),
        );
        setMessages(
          messages.map((msg) =>
            selectedMessages.includes(msg.id) ? { ...msg, read: true } : msg,
          ),
        );

        showSuccess(
          `${selectedMessages.length} bericht(en) gemarkeerd als gelezen.`,
        );
      } else if (action === "unread") {
        await updateMultipleContacts(selectedMessages, { read: false });

        // Update both arrays
        setInboxMessages(
          inboxMessages.map((msg) =>
            selectedMessages.includes(msg.id) ? { ...msg, read: false } : msg,
          ),
        );
        setArchivedMessages(
          archivedMessages.map((msg) =>
            selectedMessages.includes(msg.id) ? { ...msg, read: false } : msg,
          ),
        );
        setMessages(
          messages.map((msg) =>
            selectedMessages.includes(msg.id) ? { ...msg, read: false } : msg,
          ),
        );

        showSuccess(
          `${selectedMessages.length} bericht(en) gemarkeerd als ongelezen.`,
        );
      } else if (action === "archive") {
        await updateMultipleContacts(selectedMessages, { status: "Archive" });

        // Move all selected messages from inbox to archive
        const messagesToMove = inboxMessages.filter((msg) =>
          selectedMessages.includes(msg.id),
        );
        setInboxMessages(
          inboxMessages.filter((msg) => !selectedMessages.includes(msg.id)),
        );
        setArchivedMessages([
          ...archivedMessages,
          ...messagesToMove.map((msg) => ({ ...msg, status: "Archive" })),
        ]);

        // Update current view if in inbox
        if (activeTab === "inbox") {
          setMessages(
            messages.filter((msg) => !selectedMessages.includes(msg.id)),
          );
        }

        showSuccess(`${selectedMessages.length} bericht(en) gearchiveerd.`);
      } else if (action === "unarchive") {
        await updateMultipleContacts(selectedMessages, { status: "Inbox" });

        // Move all selected messages from archive to inbox
        const messagesToMove = archivedMessages.filter((msg) =>
          selectedMessages.includes(msg.id),
        );
        setArchivedMessages(
          archivedMessages.filter((msg) => !selectedMessages.includes(msg.id)),
        );
        setInboxMessages([
          ...inboxMessages,
          ...messagesToMove.map((msg) => ({ ...msg, status: "Inbox" })),
        ]);

        // Update current view if in archive
        if (activeTab === "archived") {
          setMessages(
            messages.filter((msg) => !selectedMessages.includes(msg.id)),
          );
        }

        showSuccess(
          `${selectedMessages.length} bericht(en) teruggezet naar inbox.`,
        );
      }

      setSelectedMessages([]);
      setSelectAll(false);
    } catch (err) {
      console.error(`Error performing batch action ${action}:`, err);
      setError(
        "Er is een fout opgetreden bij het verwerken van de geselecteerde berichten.",
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const toggleMessageSelection = (id) => {
    if (selectedMessages.includes(id)) {
      setSelectedMessages(selectedMessages.filter((msgId) => msgId !== id));
    } else {
      setSelectedMessages([...selectedMessages, id]);
    }
  };

  const toggleSelectAll = () => {
    if (selectAll) {
      setSelectedMessages([]);
    } else {
      setSelectedMessages(filteredMessages.map((msg) => msg.id));
    }
    setSelectAll(!selectAll);
  };

  const openDeleteDialog = (message) => {
    setMessageToDelete(message);
    setDeleteDialogOpen(true);
  };

  const toggleExpandMessage = (id) => {
    if (expandedMessage === id) {
      setExpandedMessage(null);
    } else {
      setExpandedMessage(id);
      // Mark as read when expanding
      const message = messages.find((msg) => msg.id === id);
      if (message && !message.read) {
        handleMarkAsRead(id);
      }
    }
  };

  const showSuccess = (message) => {
    setSuccess(message);
    setTimeout(() => setSuccess(""), 3000);
  };

  const getFilteredMessages = () => {
    let filtered = [...messages];

    // Filter by search query
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(
        (msg) =>
          msg.name?.toLowerCase().includes(query) ||
          msg.email?.toLowerCase().includes(query) ||
          msg.subject?.toLowerCase().includes(query) ||
          msg.message?.toLowerCase().includes(query),
      );
    }

    // Filter by read/unread status
    if (statusFilter === "read") {
      filtered = filtered.filter((msg) => msg.read);
    } else if (statusFilter === "unread") {
      filtered = filtered.filter((msg) => !msg.read);
    }

    // Sort messages
    filtered.sort((a, b) => {
      const dateA = new Date(a.created_at);
      const dateB = new Date(b.created_at);

      return sortOrder === "newest" ? dateB - dateA : dateA - dateB;
    });

    return filtered;
  };

  const handleGenereerOfferte = async (message) => {
    if (!message || !message.id) {
      setError("Ongeldig bericht geselecteerd.");
      return;
    }

    let selectedProductsList = null;

    if (message.quote_request_details) {
      if (Array.isArray(message.quote_request_details)) {
        selectedProductsList = message.quote_request_details;
      } else if (typeof message.quote_request_details === "string") {
        try {
          const parsed = JSON.parse(message.quote_request_details);
          if (Array.isArray(parsed)) {
            selectedProductsList = parsed;
          } else {
            setError(
              "Offerte aanvraag details zijn niet in het juiste formaat (array verwacht).",
            );
            console.error(
              "Parsed quote_request_details is not an array:",
              parsed,
            );
            return;
          }
        } catch (e) {
          setError(
            "Kon offerte aanvraag details niet lezen (JSON parse error).",
          );
          console.error("Failed to parse quote_request_details", e);
          return;
        }
      } else {
        setError("Offerte aanvraag details zijn in een onbekend formaat.");
        console.error(
          "quote_request_details is not an array or string:",
          message.quote_request_details,
        );
        return;
      }
    }

    if (!selectedProductsList || selectedProductsList.length === 0) {
      setError(
        "Geen producten gevonden in de offerte aanvraag om een offerte te genereren.",
      );
      return;
    }

    const quoteRequestPayload = {
      klantNaam: message.name,
      klantEmail: message.email,
      klantTelefoon: message.phone || null,
      klantBericht: message.message,
      selectedProducts: selectedProductsList.map((p) => ({
        productId: p.productId,
        quantity: p.quantity,
      })),
    };

    if (
      !quoteRequestPayload.selectedProducts.every(
        (p) => p.hasOwnProperty("productId") && p.hasOwnProperty("quantity"),
      )
    ) {
      setError(
        "Ongeldige productdata voor offerte generatie. Elk product moet productId en quantity hebben.",
      );
      console.error(
        "Invalid product structure in selectedProductsList:",
        selectedProductsList,
      );
      return;
    }

    setIsGeneratingOfferte(true);
    try {
      const generatedOfferte =
        await generateOfferteFromRequest(quoteRequestPayload);
      console.log("Offerte succesvol gegenereerd:", generatedOfferte);
      showSuccess(
        `Offerte ${generatedOfferte.nummer || generatedOfferte.id} succesvol aangemaakt voor ${message.name}!`,
      );
    } catch (err) {
      console.error("Error generating offerte:", err);
      const errorMessage =
        err.message ||
        (err.data && err.data.message) ||
        "Fout bij genereren offerte.";
      setError(`Fout bij genereren offerte: ${errorMessage}`);
    } finally {
      setIsGeneratingOfferte(false);
    }
  };

  const filteredMessages = getFilteredMessages();
  const unreadInboxCount = inboxMessages.filter((msg) => !msg.read).length;
  const archiveCount = archivedMessages.length;

  // Helper function to safely parse quote request details
  const getProductsFromQuoteRequest = (message) => {
    if (!message.quote_request_details) return [];

    try {
      if (Array.isArray(message.quote_request_details)) {
        return message.quote_request_details;
      } else if (typeof message.quote_request_details === "string") {
        const parsed = JSON.parse(message.quote_request_details);
        return Array.isArray(parsed) ? parsed : [];
      }
      return [];
    } catch (e) {
      console.error("Error parsing quote_request_details", e);
      return [];
    }
  };

  // Shared message detail content renderer
  const renderMessageDetailContent = (message) => (
    <div className="space-y-4">
      {/* Contact info */}
      <div
        className={`rounded-lg p-4 ${isDark ? "bg-white/[0.03] border border-white/[0.06]" : "bg-gray-50 border border-gray-100"}`}
      >
        <p
          className={`text-[11px] font-medium uppercase tracking-wider mb-3 ${isDark ? "text-gray-500" : "text-gray-400"}`}
        >
          Contactgegevens
        </p>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
          <div>
            <p
              className={`text-[11px] ${isDark ? "text-gray-600" : "text-gray-400"}`}
            >
              Naam
            </p>
            <p
              className={`text-[13px] font-medium ${isDark ? "text-gray-200" : "text-gray-800"}`}
            >
              {message.name}
            </p>
          </div>
          <div>
            <p
              className={`text-[11px] ${isDark ? "text-gray-600" : "text-gray-400"}`}
            >
              E-mail
            </p>
            <a
              href={`mailto:${message.email}`}
              className={`text-[13px] font-medium ${isDark ? "text-blue-400 hover:text-blue-300" : "text-blue-600 hover:text-blue-700"}`}
              onClick={(e) => e.stopPropagation()}
            >
              {message.email}
            </a>
          </div>
          {message.phone && (
            <div>
              <p
                className={`text-[11px] ${isDark ? "text-gray-600" : "text-gray-400"}`}
              >
                Telefoon
              </p>
              <a
                href={`tel:${message.phone}`}
                className={`text-[13px] font-medium ${isDark ? "text-blue-400 hover:text-blue-300" : "text-blue-600 hover:text-blue-700"}`}
                onClick={(e) => e.stopPropagation()}
              >
                {message.phone}
              </a>
            </div>
          )}
          <div>
            <p
              className={`text-[11px] ${isDark ? "text-gray-600" : "text-gray-400"}`}
            >
              {message.type === "sent" ? "Verzonden" : "Ontvangen"}
            </p>
            <p
              className={`text-[13px] ${isDark ? "text-gray-300" : "text-gray-700"}`}
            >
              {formatMessageDate(message.created_at)}
            </p>
          </div>
        </div>
      </div>

      {/* Message body */}
      <div>
        <p
          className={`text-[11px] font-medium uppercase tracking-wider mb-2 ${isDark ? "text-gray-500" : "text-gray-400"}`}
        >
          Bericht
        </p>
        <div
          className={`rounded-lg p-4 text-[13px] leading-relaxed whitespace-pre-wrap ${isDark ? "bg-white/[0.03] border border-white/[0.06] text-gray-300" : "bg-gray-50 border border-gray-100 text-gray-700"}`}
        >
          {message.type === "sent" ? (
            <div dangerouslySetInnerHTML={{ __html: message.message }} />
          ) : (
            message.message || (
              <span
                className={
                  isDark ? "text-gray-600 italic" : "text-gray-400 italic"
                }
              >
                Geen bericht
              </span>
            )
          )}
        </div>
      </div>

      {/* Quote request details */}
      {message.subject?.toLowerCase().includes("offerte") &&
        message.quote_request_details && (
          <div>
            <p
              className={`text-[11px] font-medium uppercase tracking-wider mb-2 ${isDark ? "text-gray-500" : "text-gray-400"}`}
            >
              Offerte aanvraag
            </p>
            <div
              className={`rounded-lg overflow-hidden ${isDark ? "border border-white/[0.06]" : "border border-gray-100"}`}
            >
              {getProductsFromQuoteRequest(message).length > 0 ? (
                <div
                  className={`divide-y ${isDark ? "divide-white/[0.04]" : "divide-gray-50"}`}
                >
                  {getProductsFromQuoteRequest(message).map(
                    (product, index) => (
                      <div
                        key={index}
                        className={`flex items-center justify-between px-4 py-3 ${isDark ? "bg-white/[0.02]" : "bg-gray-50"}`}
                      >
                        <div>
                          <p
                            className={`text-[13px] font-medium ${isDark ? "text-gray-200" : "text-gray-800"}`}
                          >
                            {product.productName ||
                              `Product ${product.productId}`}
                          </p>
                        </div>
                        <span
                          className={`text-[12px] font-medium px-2 py-0.5 rounded-md ${isDark ? "bg-white/[0.06] text-gray-300" : "bg-gray-200/60 text-gray-600"}`}
                        >
                          {product.quantity}x
                        </span>
                      </div>
                    ),
                  )}
                </div>
              ) : (
                <p
                  className={`px-4 py-3 text-[12px] italic ${isDark ? "text-gray-600" : "text-gray-400"}`}
                >
                  Geen producten geselecteerd.
                </p>
              )}
            </div>
          </div>
        )}

      {/* Actions */}
      <div
        className={`flex items-center justify-end gap-2 pt-3 border-t ${isDark ? "border-white/[0.06]" : "border-gray-100"}`}
      >
        {message.subject?.toLowerCase().includes("offerte") &&
          activeTab === "inbox" &&
          getProductsFromQuoteRequest(message).length > 0 && (
            <Button
              size="sm"
              onClick={(e) => {
                e.stopPropagation();
                handleGenereerOfferte(message);
              }}
              disabled={isGeneratingOfferte}
              className={`rounded-lg h-7 px-3 text-[11px] font-medium ${isDark ? "bg-white text-black hover:bg-gray-200" : "bg-gray-900 text-white hover:bg-gray-800"}`}
            >
              <FileText className="h-3 w-3 mr-1" />
              {isGeneratingOfferte ? "Genereren..." : "Offerte"}
            </Button>
          )}
        {activeTab === "inbox" ? (
          <>
            <button
              onClick={(e) => {
                e.stopPropagation();
                message.read
                  ? handleMarkAsUnread(message.id)
                  : handleMarkAsRead(message.id);
              }}
              className={`h-7 px-2.5 rounded-md text-[11px] font-medium transition-colors ${isDark ? "text-gray-400 hover:text-gray-200 hover:bg-white/[0.04]" : "text-gray-500 hover:text-gray-700 hover:bg-gray-50"}`}
            >
              {message.read ? "Ongelezen" : "Gelezen"}
            </button>
            <button
              onClick={(e) => {
                e.stopPropagation();
                handleArchiveMessage(message.id);
              }}
              className={`h-7 px-2.5 rounded-md text-[11px] font-medium transition-colors ${isDark ? "text-gray-400 hover:text-gray-200 hover:bg-white/[0.04]" : "text-gray-500 hover:text-gray-700 hover:bg-gray-50"}`}
            >
              Archiveren
            </button>
          </>
        ) : activeTab === "archived" ? (
          <button
            onClick={(e) => {
              e.stopPropagation();
              handleUnarchiveMessage(message.id);
            }}
            className={`h-7 px-2.5 rounded-md text-[11px] font-medium transition-colors ${isDark ? "text-gray-400 hover:text-gray-200 hover:bg-white/[0.04]" : "text-gray-500 hover:text-gray-700 hover:bg-gray-50"}`}
          >
            Terugzetten
          </button>
        ) : null}
        <button
          onClick={(e) => {
            e.stopPropagation();
            openDeleteDialog(message);
          }}
          className="h-7 px-2.5 rounded-md text-[11px] font-medium text-red-400 hover:text-red-500 hover:bg-red-500/5 transition-colors"
        >
          Verwijderen
        </button>
      </div>
    </div>
  );

  // Bottom sheet component for mobile message detail
  const MobileBottomSheet = ({ message, onClose }) => {
    const sheetY = useMotionValue(0);
    const sheetRef = useRef(null);
    const contentRef = useRef(null);
    const dragControls = useDragControls();
    const isScrolledToTop = useRef(true);
    const isDragging = useRef(false);

    useEffect(() => {
      document.body.style.overflow = "hidden";
      return () => {
        document.body.style.overflow = "";
      };
    }, []);

    const handleContentScroll = (e) => {
      isScrolledToTop.current = e.target.scrollTop <= 0;
    };

    const handleContentPointerDown = (e) => {
      if (isScrolledToTop.current) {
        isDragging.current = true;
        dragControls.start(e);
      }
    };

    return (
      <div className="fixed inset-0 z-[100]">
        {/* Backdrop */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.2 }}
          className="absolute inset-0 bg-black/60 backdrop-blur-sm"
          onClick={onClose}
        />
        {/* Sheet */}
        <motion.div
          ref={sheetRef}
          initial={{ y: "100%" }}
          animate={{ y: 0 }}
          exit={{ y: "100%" }}
          transition={{ type: "spring", damping: 35, stiffness: 400 }}
          drag="y"
          dragConstraints={{ top: 0, bottom: 0 }}
          dragElastic={{ top: 0, bottom: 0.6 }}
          dragListener={false}
          dragControls={dragControls}
          onDragEnd={(_, info) => {
            if (info.offset.y > 80 || info.velocity.y > 500) {
              onClose();
            }
          }}
          className={`absolute bottom-0 left-0 right-0 rounded-t-2xl flex flex-col ${isDark ? "bg-[#141414] border-t border-white/[0.08]" : "bg-white border-t border-gray-200"}`}
          style={{ y: sheetY, maxHeight: "85vh" }}
        >
          {/* Drag handle — always draggable */}
          <div
            onPointerDown={(e) => dragControls.start(e)}
            className="flex justify-center pt-3 pb-2 cursor-grab active:cursor-grabbing touch-none shrink-0"
            style={{ touchAction: "none" }}
          >
            <div
              className={`w-10 h-1 rounded-full ${isDark ? "bg-white/20" : "bg-gray-300"}`}
            />
          </div>

          {/* Sheet header */}
          <div
            className={`px-5 pb-3 border-b shrink-0 ${isDark ? "border-white/[0.06]" : "border-gray-100"}`}
          >
            <div className="flex items-center justify-between">
              <div className="min-w-0 flex-1 mr-3">
                <h3
                  className={`text-[15px] font-semibold truncate ${isDark ? "text-white" : "text-gray-900"}`}
                >
                  {message.subject}
                </h3>
                <p
                  className={`text-[12px] mt-0.5 ${isDark ? "text-gray-500" : "text-gray-400"}`}
                >
                  Van {message.name}
                </p>
              </div>
              <button
                onClick={onClose}
                className={`flex-shrink-0 h-8 w-8 rounded-full flex items-center justify-center transition-colors ${isDark ? "hover:bg-white/[0.06] text-gray-400" : "hover:bg-gray-100 text-gray-500"}`}
              >
                <X className="h-4 w-4" />
              </button>
            </div>
          </div>

          {/* Sheet content — swipeable when scrolled to top */}
          <div
            ref={contentRef}
            onScroll={handleContentScroll}
            onPointerDown={handleContentPointerDown}
            className="overflow-y-auto overscroll-contain p-5 flex-1"
            style={{
              WebkitOverflowScrolling: "touch",
              touchAction: isScrolledToTop.current ? "none" : "pan-y",
            }}
          >
            {renderMessageDetailContent(message)}
          </div>
        </motion.div>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div
          className={`w-5 h-5 rounded-full border-2 ${isDark ? "border-white/10 border-t-white/60" : "border-gray-200 border-t-gray-500"} animate-spin`}
        />
      </div>
    );
  }

  const tabItems = [
    { id: "inbox", label: "Inbox", count: unreadInboxCount },
    { id: "archived", label: "Archief", count: archiveCount },
    { id: "sent", label: "Verzonden", count: 0 },
  ];

  return (
    <div className="space-y-6">
      {/* Header */}
      <motion.div
        initial={{ opacity: 0, y: 8 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3 }}
      >
        <div className="flex items-center justify-between">
          <div>
            <h1
              className={`text-2xl font-semibold tracking-tight ${isDark ? "text-white" : "text-gray-900"}`}
            >
              Inbox
            </h1>
            <p
              className={`mt-1 text-sm ${isDark ? "text-gray-500" : "text-gray-500"}`}
            >
              Bekijk en beheer alle berichten van je contactformulier.
            </p>
          </div>

          <div className="flex items-center gap-2">
            <button
              onClick={fetchMessages}
              className={`p-2 rounded-lg transition-colors ${isDark ? "text-gray-500 hover:text-gray-300 hover:bg-white/[0.04]" : "text-gray-400 hover:text-gray-600 hover:bg-gray-50"}`}
            >
              <RefreshCcw className="h-4 w-4" />
            </button>

            <button
              onClick={() => setSettingsOpen(true)}
              className={`p-2 rounded-lg transition-colors ${isDark ? "text-gray-500 hover:text-gray-300 hover:bg-white/[0.04]" : "text-gray-400 hover:text-gray-600 hover:bg-gray-50"}`}
            >
              <Settings className="h-4 w-4" />
            </button>

            <Button
              size="sm"
              onClick={() => setComposeOpen(true)}
              className={`rounded-lg h-8 px-3 text-[12px] font-medium ${isDark ? "bg-white text-black hover:bg-gray-200" : "bg-gray-900 text-white hover:bg-gray-800"}`}
            >
              <Send className="h-3.5 w-3.5 mr-1.5" />
              Nieuw
            </Button>

            <SMTPSettingsDialog
              open={settingsOpen}
              onOpenChange={setSettingsOpen}
            />
            <ComposeEmailDialog
              open={composeOpen}
              onOpenChange={setComposeOpen}
            />
          </div>
        </div>
      </motion.div>

      {/* Notifications */}
      <AnimatePresence>
        {error && (
          <motion.div
            initial={{ opacity: 0, y: -4 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -4 }}
          >
            <div
              className={`flex items-center gap-2.5 px-4 py-3 rounded-lg text-[13px] ${isDark ? "bg-red-500/8 border border-red-500/15 text-red-300" : "bg-red-50 border border-red-100 text-red-700"}`}
            >
              <AlertCircle className="h-4 w-4 flex-shrink-0" />
              <span>{error}</span>
            </div>
          </motion.div>
        )}
        {success && (
          <motion.div
            initial={{ opacity: 0, y: -4 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -4 }}
          >
            <div
              className={`flex items-center gap-2.5 px-4 py-3 rounded-lg text-[13px] ${isDark ? "bg-[#BFFF00]/8 border border-[#BFFF00]/15 text-[#BFFF00]" : "bg-emerald-50 border border-emerald-100 text-emerald-700"}`}
            >
              <Check className="h-4 w-4 flex-shrink-0" />
              <span>{success}</span>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Tabs + Filters */}
      <motion.div
        initial={{ opacity: 0, y: 8 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3, delay: 0.05 }}
        className="flex flex-col gap-3"
      >
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          {/* Tab buttons */}
          <div
            className={`inline-flex items-center gap-0.5 p-0.5 rounded-lg ${isDark ? "bg-white/[0.04]" : "bg-gray-100"}`}
          >
            {tabItems.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`relative px-3 py-1.5 rounded-md text-[12px] font-medium transition-all duration-150 ${
                  activeTab === tab.id
                    ? isDark
                      ? "bg-white/[0.1] text-white"
                      : "bg-white text-gray-900 shadow-sm"
                    : isDark
                      ? "text-gray-500 hover:text-gray-300"
                      : "text-gray-500 hover:text-gray-700"
                }`}
              >
                {tab.label}
                {tab.count > 0 && (
                  <span
                    className={`ml-1.5 text-[10px] font-semibold px-1.5 py-0.5 rounded-md ${
                      activeTab === tab.id
                        ? isDark
                          ? "bg-white/10 text-white"
                          : "bg-black/[0.06] text-gray-600"
                        : isDark
                          ? "bg-white/[0.06] text-gray-500"
                          : "bg-gray-200/80 text-gray-500"
                    }`}
                  >
                    {tab.count}
                  </span>
                )}
              </button>
            ))}
          </div>

          {/* Search + Filters */}
          <div className="flex items-center gap-2">
            <div className="relative flex-1 sm:w-52">
              <Search
                className={`absolute left-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 ${isDark ? "text-gray-600" : "text-gray-400"}`}
              />
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Zoeken..."
                className={`w-full h-8 pl-8 pr-3 rounded-lg text-[12px] outline-none transition-colors ${
                  isDark
                    ? "bg-white/[0.04] border border-white/[0.06] text-white placeholder-gray-600 focus:border-white/15"
                    : "bg-gray-50 border border-gray-200 text-gray-900 placeholder-gray-400 focus:border-gray-300 focus:bg-white"
                }`}
              />
            </div>

            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger
                className={`w-[100px] h-8 rounded-lg text-[12px] ${isDark ? "bg-white/[0.04] border-white/[0.06] text-gray-300" : "bg-gray-50 border-gray-200 text-gray-600"}`}
              >
                <div className="flex items-center gap-1">
                  <Filter className="h-3 w-3" />
                  <SelectValue />
                </div>
              </SelectTrigger>
              <SelectContent
                className={`rounded-lg ${isDark ? "bg-[#141414] border-white/[0.08]" : ""}`}
              >
                <SelectItem value="all">Alle</SelectItem>
                <SelectItem value="read">Gelezen</SelectItem>
                <SelectItem value="unread">Ongelezen</SelectItem>
              </SelectContent>
            </Select>

            <Select value={sortOrder} onValueChange={setSortOrder}>
              <SelectTrigger
                className={`w-[100px] h-8 rounded-lg text-[12px] ${isDark ? "bg-white/[0.04] border-white/[0.06] text-gray-300" : "bg-gray-50 border-gray-200 text-gray-600"}`}
              >
                <div className="flex items-center gap-1">
                  <Clock className="h-3 w-3" />
                  <SelectValue />
                </div>
              </SelectTrigger>
              <SelectContent
                className={`rounded-lg ${isDark ? "bg-[#141414] border-white/[0.08]" : ""}`}
              >
                <SelectItem value="newest">Nieuwst</SelectItem>
                <SelectItem value="oldest">Oudst</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        {/* Batch actions */}
        <AnimatePresence>
          {selectedMessages.length > 0 && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: "auto" }}
              exit={{ opacity: 0, height: 0 }}
              className={`flex items-center justify-between gap-3 px-4 py-2.5 rounded-lg ${isDark ? "bg-white/[0.04] border border-white/[0.06]" : "bg-gray-50 border border-gray-200/80"}`}
            >
              <span
                className={`text-[12px] font-medium ${isDark ? "text-gray-300" : "text-gray-600"}`}
              >
                {selectedMessages.length} geselecteerd
              </span>
              <div className="flex items-center gap-1.5">
                {activeTab === "inbox" ? (
                  <>
                    <button
                      onClick={() => handleBatchAction("read")}
                      disabled={isSubmitting}
                      className={`px-2.5 py-1 rounded-md text-[11px] font-medium transition-colors ${isDark ? "text-gray-300 hover:bg-white/[0.06]" : "text-gray-600 hover:bg-gray-100"}`}
                    >
                      Gelezen
                    </button>
                    <button
                      onClick={() => handleBatchAction("archive")}
                      disabled={isSubmitting}
                      className={`px-2.5 py-1 rounded-md text-[11px] font-medium transition-colors ${isDark ? "text-gray-300 hover:bg-white/[0.06]" : "text-gray-600 hover:bg-gray-100"}`}
                    >
                      Archiveren
                    </button>
                  </>
                ) : activeTab === "archived" ? (
                  <button
                    onClick={() => handleBatchAction("unarchive")}
                    disabled={isSubmitting}
                    className={`px-2.5 py-1 rounded-md text-[11px] font-medium transition-colors ${isDark ? "text-gray-300 hover:bg-white/[0.06]" : "text-gray-600 hover:bg-gray-100"}`}
                  >
                    Terugzetten
                  </button>
                ) : null}
                <button
                  onClick={() => setSelectedMessages([])}
                  className={`p-1 rounded-md transition-colors ${isDark ? "text-gray-500 hover:text-gray-300" : "text-gray-400 hover:text-gray-600"}`}
                >
                  <X className="h-3.5 w-3.5" />
                </button>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </motion.div>

      {/* Delete dialog */}
      <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <DialogContent
          className={`rounded-xl ${isDark ? "bg-[#141414] border-white/[0.08]" : "bg-white border-gray-200"}`}
        >
          <DialogHeader>
            <DialogTitle
              className={`text-[14px] font-semibold ${isDark ? "text-white" : "text-gray-900"}`}
            >
              Bericht verwijderen
            </DialogTitle>
            <DialogDescription
              className={`text-[13px] ${isDark ? "text-gray-500" : "text-gray-500"}`}
            >
              Weet je zeker dat je het bericht van{" "}
              <span
                className={`font-medium ${isDark ? "text-gray-300" : "text-gray-700"}`}
              >
                {messageToDelete?.name}
              </span>{" "}
              wilt verwijderen?
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setDeleteDialogOpen(false)}
              className={`rounded-lg text-[12px] ${isDark ? "border-white/[0.08] text-gray-300 hover:bg-white/[0.04]" : "border-gray-200 text-gray-600 hover:bg-gray-50"}`}
            >
              Annuleren
            </Button>
            <Button
              size="sm"
              onClick={handleDeleteMessage}
              disabled={isSubmitting}
              className="rounded-lg text-[12px] bg-red-500 hover:bg-red-600 text-white"
            >
              {isSubmitting ? "Verwijderen..." : "Verwijderen"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Messages list */}
      <motion.div
        initial={{ opacity: 0, y: 8 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3, delay: 0.1 }}
      >
        {filteredMessages.length === 0 ? (
          <div
            className={`rounded-xl py-16 text-center ${isDark ? "bg-white/[0.03] border border-white/[0.06]" : "bg-white border border-gray-200/80"}`}
          >
            <Mail
              className={`h-8 w-8 mx-auto mb-3 ${isDark ? "text-gray-700" : "text-gray-300"}`}
            />
            <p
              className={`text-[13px] font-medium ${isDark ? "text-gray-400" : "text-gray-500"}`}
            >
              {searchQuery
                ? "Geen resultaten gevonden."
                : activeTab === "inbox"
                  ? "Je inbox is leeg."
                  : activeTab === "archived"
                    ? "Geen gearchiveerde berichten."
                    : "Geen verzonden berichten."}
            </p>
            {searchQuery && (
              <button
                onClick={() => setSearchQuery("")}
                className={`mt-2 text-[12px] font-medium ${isDark ? "text-gray-500 hover:text-gray-300" : "text-gray-400 hover:text-gray-600"}`}
              >
                Zoekopdracht wissen
              </button>
            )}
          </div>
        ) : (
          <div
            className={`rounded-xl overflow-hidden ${isDark ? "bg-white/[0.03] border border-white/[0.06]" : "bg-white border border-gray-200/80"}`}
          >
            {/* List header — hidden on mobile */}
            <div
              className={`hidden sm:grid grid-cols-12 px-4 py-2.5 text-[11px] font-medium uppercase tracking-wider border-b ${isDark ? "text-gray-600 border-white/[0.06]" : "text-gray-400 border-gray-100"}`}
            >
              <div className="col-span-1 flex items-center">
                <Checkbox
                  checked={selectAll && filteredMessages.length > 0}
                  onCheckedChange={toggleSelectAll}
                />
              </div>
              <div className="col-span-3">Afzender</div>
              <div className="col-span-5">Onderwerp</div>
              <div className="col-span-2">Datum</div>
              <div className="col-span-1" />
            </div>

            {/* Mobile select-all bar */}
            {isMobile && (
              <div
                className={`flex items-center px-4 py-2 border-b ${isDark ? "border-white/[0.06]" : "border-gray-100"}`}
              >
                <Checkbox
                  checked={selectAll && filteredMessages.length > 0}
                  onCheckedChange={toggleSelectAll}
                />
                <span
                  className={`text-[11px] ml-2 ${isDark ? "text-gray-600" : "text-gray-400"}`}
                >
                  Selecteer alles
                </span>
              </div>
            )}

            {/* Messages */}
            <div
              className={`divide-y ${isDark ? "divide-white/[0.04]" : "divide-gray-50"}`}
            >
              {filteredMessages.map((message) => (
                <div key={message.id}>
                  {/* Desktop row (sm+) */}
                  <div
                    className={`hidden sm:grid grid-cols-12 px-4 py-3 cursor-pointer transition-colors duration-100 ${
                      expandedMessage === message.id
                        ? isDark
                          ? "bg-white/[0.04]"
                          : "bg-gray-50/80"
                        : !message.read
                          ? isDark
                            ? "bg-white/[0.02]"
                            : "bg-blue-50/20"
                          : isDark
                            ? "hover:bg-white/[0.02]"
                            : "hover:bg-gray-50/50"
                    }`}
                    onClick={() => toggleExpandMessage(message.id)}
                  >
                    <div
                      className="col-span-1 flex items-center"
                      onClick={(e) => e.stopPropagation()}
                    >
                      <Checkbox
                        checked={selectedMessages.includes(message.id)}
                        onCheckedChange={() =>
                          toggleMessageSelection(message.id)
                        }
                      />
                    </div>

                    <div className="col-span-3 flex items-center min-w-0">
                      <div className="min-w-0">
                        <p
                          className={`text-[13px] truncate ${!message.read ? "font-semibold" : "font-medium"} ${isDark ? "text-gray-200" : "text-gray-800"}`}
                        >
                          {message.name}
                        </p>
                        <p
                          className={`text-[11px] truncate ${isDark ? "text-gray-600" : "text-gray-400"}`}
                        >
                          {message.email}
                        </p>
                      </div>
                    </div>

                    <div className="col-span-5 flex items-center min-w-0 gap-2">
                      {!message.read && (
                        <div className="h-1.5 w-1.5 rounded-full bg-[#BFFF00] flex-shrink-0" />
                      )}
                      <span
                        className={`text-[13px] truncate ${!message.read ? "font-medium" : ""} ${isDark ? "text-gray-300" : "text-gray-700"}`}
                      >
                        {message.subject}
                      </span>
                    </div>

                    <div
                      className={`flex col-span-2 items-center text-[11px] ${isDark ? "text-gray-600" : "text-gray-400"}`}
                    >
                      {formatMessageDate(message.created_at)}
                    </div>

                    <div className="col-span-1 flex items-center justify-end">
                      <ChevronDown
                        className={`h-3.5 w-3.5 transition-transform duration-200 ${
                          expandedMessage === message.id ? "rotate-180" : ""
                        } ${isDark ? "text-gray-600" : "text-gray-300"}`}
                      />
                    </div>
                  </div>

                  {/* Mobile row */}
                  <div
                    className={`sm:hidden flex items-start gap-3 px-4 py-3 cursor-pointer transition-colors duration-100 ${
                      !message.read
                        ? isDark
                          ? "bg-white/[0.02]"
                          : "bg-blue-50/20"
                        : isDark
                          ? "hover:bg-white/[0.02]"
                          : "hover:bg-gray-50/50"
                    }`}
                    onClick={() => toggleExpandMessage(message.id)}
                  >
                    <div
                      className="pt-0.5 flex-shrink-0"
                      onClick={(e) => e.stopPropagation()}
                    >
                      <Checkbox
                        checked={selectedMessages.includes(message.id)}
                        onCheckedChange={() =>
                          toggleMessageSelection(message.id)
                        }
                      />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between gap-2">
                        <div className="flex items-center gap-1.5 min-w-0">
                          {!message.read && (
                            <div className="h-1.5 w-1.5 rounded-full bg-[#BFFF00] flex-shrink-0" />
                          )}
                          <p
                            className={`text-[13px] truncate ${!message.read ? "font-semibold" : "font-medium"} ${isDark ? "text-gray-200" : "text-gray-800"}`}
                          >
                            {message.name}
                          </p>
                        </div>
                        <span
                          className={`text-[11px] flex-shrink-0 ${isDark ? "text-gray-600" : "text-gray-400"}`}
                        >
                          {formatMessageDate(message.created_at)}
                        </span>
                      </div>
                      <p
                        className={`text-[12px] mt-0.5 truncate ${isDark ? "text-gray-400" : "text-gray-500"}`}
                      >
                        {message.subject}
                      </p>
                      {message.message && (
                        <p
                          className={`text-[11px] mt-0.5 truncate ${isDark ? "text-gray-600" : "text-gray-400"}`}
                        >
                          {typeof message.message === "string"
                            ? message.message
                                .replace(/<[^>]*>/g, "")
                                .slice(0, 80)
                            : ""}
                        </p>
                      )}
                    </div>
                    <ChevronDown
                      className={`h-3.5 w-3.5 flex-shrink-0 mt-1 transition-transform duration-200 ${isDark ? "text-gray-600" : "text-gray-300"}`}
                      style={{
                        transform:
                          expandedMessage === message.id
                            ? "rotate(180deg)"
                            : "none",
                      }}
                    />
                  </div>

                  {/* Expanded detail — desktop only (inline) */}
                  {!isMobile && (
                    <AnimatePresence>
                      {expandedMessage === message.id && (
                        <motion.div
                          initial={{ opacity: 0, height: 0 }}
                          animate={{ opacity: 1, height: "auto" }}
                          exit={{ opacity: 0, height: 0 }}
                          transition={{ duration: 0.2 }}
                          className="overflow-hidden"
                        >
                          <div
                            className={`px-4 pb-4 ${isDark ? "border-t border-white/[0.04]" : "border-t border-gray-100"}`}
                          >
                            <div className="ml-8 pt-4">
                              {renderMessageDetailContent(message)}
                            </div>
                          </div>
                        </motion.div>
                      )}
                    </AnimatePresence>
                  )}
                </div>
              ))}
            </div>

            {/* Footer */}
            <div
              className={`px-4 py-2.5 border-t flex items-center justify-between ${isDark ? "border-white/[0.06]" : "border-gray-100"}`}
            >
              <span
                className={`text-[11px] ${isDark ? "text-gray-600" : "text-gray-400"}`}
              >
                {filteredMessages.length}{" "}
                {filteredMessages.length === 1 ? "bericht" : "berichten"}
              </span>
              {(searchQuery || statusFilter !== "all") && (
                <div className="flex gap-2">
                  {searchQuery && (
                    <button
                      onClick={() => setSearchQuery("")}
                      className={`text-[11px] ${isDark ? "text-gray-500 hover:text-gray-300" : "text-gray-400 hover:text-gray-600"}`}
                    >
                      Zoek wissen
                    </button>
                  )}
                  {statusFilter !== "all" && (
                    <button
                      onClick={() => setStatusFilter("all")}
                      className={`text-[11px] ${isDark ? "text-gray-500 hover:text-gray-300" : "text-gray-400 hover:text-gray-600"}`}
                    >
                      Filter wissen
                    </button>
                  )}
                </div>
              )}
            </div>
          </div>
        )}
      </motion.div>

      {/* Mobile bottom sheet for message detail */}
      <AnimatePresence>
        {isMobile &&
          expandedMessage &&
          (() => {
            const message = filteredMessages.find(
              (m) => m.id === expandedMessage,
            );
            return message ? (
              <MobileBottomSheet
                key={message.id}
                message={message}
                onClose={() => setExpandedMessage(null)}
              />
            ) : null;
          })()}
      </AnimatePresence>
    </div>
  );
};

export default InboxManager;
